import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { FaceLandmark } from 'src/app/user-identification/models/life-check';

/** Índices MediaPipe adicionales para volumen / parallax (lab NIF-567). */
export enum Face3dLabLandmark {
	NoseTip = FaceLandmark.Nose,
	Forehead = FaceLandmark.Forehead,
	Chin = FaceLandmark.Chin,
	LeftEar = FaceLandmark.LeftEar,
	RightEar = FaceLandmark.RightEar,
	LeftCheek = 50,
	RightCheek = 280,
}

/** Offset 2D respecto al centro de cara (coords normalizadas del frame de análisis). */
export interface FaceKeyOffset {
	dx: number;
	dy: number;
}

/** Snapshot geométrico mínimo usado por depth-real y turns-3d. */
export interface FaceGeometrySnapshot {
	bboxHeight: number;
	bboxScale: number;
	centerX: number;
	centerY: number;
	zRange: number;
	noseToLeftCheek: number;
	noseToRightCheek: number;
	parallaxAsymmetry: number;
	/**
	 * Offsets de landmarks clave respecto al centro (mismo orden siempre).
	 * Usados para residual de homotecia far↔near.
	 */
	keyOffsets: FaceKeyOffset[];
}

/** Parche luma normalizado (media 0) para comparar textura far/near. */
export interface FaceRoiTextureMetrics {
	/** Varianza del laplaciano 3×3 en el ROI reescalado. */
	laplacianVar: number;
	/** Parche 16×16 mean-centered (256 floats); solo memoria de sesión. */
	patch16: Float32Array;
}

export const FACE_TEXTURE_PATCH_SIZE = 16;

const KEY_LANDMARK_ORDER: Face3dLabLandmark[] = [
	Face3dLabLandmark.NoseTip,
	Face3dLabLandmark.Forehead,
	Face3dLabLandmark.Chin,
	Face3dLabLandmark.LeftEar,
	Face3dLabLandmark.RightEar,
	Face3dLabLandmark.LeftCheek,
	Face3dLabLandmark.RightCheek,
];

function dist2d(a: NormalizedLandmark, b: NormalizedLandmark): number {
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	return Math.hypot(dx, dy);
}

export function clamp01(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}
	return Math.min(1, Math.max(0, value));
}

export function scoreFromRatio(value: number, minPass: number, fullPass: number): number {
	if (fullPass <= minPass) {
		return value >= minPass ? 100 : 0;
	}
	if (value <= minPass) {
		return 0;
	}
	if (value >= fullPass) {
		return 100;
	}
	return clamp01((value - minPass) / (fullPass - minPass)) * 100;
}

export function extractFaceGeometrySnapshot(landmarks: NormalizedLandmark[]): FaceGeometrySnapshot | null {
	if (!landmarks?.length || landmarks.length < 455) {
		return null;
	}

	const nose = landmarks[Face3dLabLandmark.NoseTip];
	const forehead = landmarks[Face3dLabLandmark.Forehead];
	const chin = landmarks[Face3dLabLandmark.Chin];
	const leftEar = landmarks[Face3dLabLandmark.LeftEar];
	const rightEar = landmarks[Face3dLabLandmark.RightEar];
	const leftCheek = landmarks[Face3dLabLandmark.LeftCheek];
	const rightCheek = landmarks[Face3dLabLandmark.RightCheek];

	if (!nose || !forehead || !chin || !leftEar || !rightEar || !leftCheek || !rightCheek) {
		return null;
	}

	const keyPoints = [nose, forehead, chin, leftEar, rightEar, leftCheek, rightCheek];
	let minX = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	const zs: number[] = [];

	for (const p of keyPoints) {
		minX = Math.min(minX, p.x);
		maxX = Math.max(maxX, p.x);
		minY = Math.min(minY, p.y);
		maxY = Math.max(maxY, p.y);
		zs.push(typeof p.z === 'number' ? p.z : 0);
	}

	const bboxWidth = Math.max(0.001, maxX - minX);
	const bboxHeight = Math.max(0.001, maxY - minY);
	const bboxScale = Math.hypot(bboxWidth, bboxHeight);
	const centerX = (minX + maxX) / 2;
	const centerY = (minY + maxY) / 2;
	const zRange = Math.max(...zs) - Math.min(...zs);
	const faceWidth = Math.max(0.001, dist2d(leftEar, rightEar));
	const noseToLeftCheek = dist2d(nose, leftCheek);
	const noseToRightCheek = dist2d(nose, rightCheek);
	const parallaxAsymmetry = Math.abs(noseToLeftCheek - noseToRightCheek) / faceWidth;

	const keyOffsets: FaceKeyOffset[] = KEY_LANDMARK_ORDER.map((idx) => {
		const p = landmarks[idx];
		return { dx: p.x - centerX, dy: p.y - centerY };
	});

	return {
		bboxHeight,
		bboxScale,
		centerX,
		centerY,
		zRange,
		noseToLeftCheek,
		noseToRightCheek,
		parallaxAsymmetry,
		keyOffsets,
	};
}

/**
 * Residual RMSE tras la mejor homotecia (escala) far→near sobre offsets.
 * Foto plana ≈ 0 (escala pura). Cara 3D / perspectiva → residual mayor.
 */
export function computeSimilarityResidual(far: FaceKeyOffset[], near: FaceKeyOffset[]): number {
	const n = Math.min(far.length, near.length);
	if (n < 3) {
		return 0;
	}
	let dot = 0;
	let farEnergy = 0;
	for (let i = 0; i < n; i++) {
		dot += far[i].dx * near[i].dx + far[i].dy * near[i].dy;
		farEnergy += far[i].dx * far[i].dx + far[i].dy * far[i].dy;
	}
	if (farEnergy < 1e-12) {
		return 0;
	}
	const scale = dot / farEnergy;
	let err = 0;
	let nearEnergy = 0;
	for (let i = 0; i < n; i++) {
		const ex = scale * far[i].dx - near[i].dx;
		const ey = scale * far[i].dy - near[i].dy;
		err += ex * ex + ey * ey;
		nearEnergy += near[i].dx * near[i].dx + near[i].dy * near[i].dy;
	}
	const rmse = Math.sqrt(err / n);
	const norm = Math.sqrt(nearEnergy / n);
	return norm > 1e-9 ? rmse / norm : rmse;
}

/** NCC en [-1,1] entre dos parches mean-centered del mismo tamaño. */
export function normalizedCrossCorrelation(a: Float32Array, b: Float32Array): number {
	const n = Math.min(a.length, b.length);
	if (n < 4) {
		return 1;
	}
	let dot = 0;
	let ea = 0;
	let eb = 0;
	for (let i = 0; i < n; i++) {
		dot += a[i] * b[i];
		ea += a[i] * a[i];
		eb += b[i] * b[i];
	}
	const denom = Math.sqrt(ea * eb);
	if (denom < 1e-9) {
		return 1;
	}
	return Math.max(-1, Math.min(1, dot / denom));
}

/**
 * Extrae métricas de textura del ROI facial en el canvas de análisis.
 * El parche se reescala a 16×16 (agregado; no se persiste fuera de sesión).
 */
export function extractFaceRoiTextureMetrics(
	ctx: CanvasRenderingContext2D,
	bbox: { x0: number; y0: number; x1: number; y1: number },
): FaceRoiTextureMetrics | null {
	const canvas = ctx.canvas;
	const w = canvas.width;
	const h = canvas.height;
	if (w < 8 || h < 8) {
		return null;
	}
	const x0 = Math.max(0, Math.floor(bbox.x0 * w));
	const y0 = Math.max(0, Math.floor(bbox.y0 * h));
	const x1 = Math.min(w, Math.ceil(bbox.x1 * w));
	const y1 = Math.min(h, Math.ceil(bbox.y1 * h));
	const rw = x1 - x0;
	const rh = y1 - y0;
	if (rw < 8 || rh < 8) {
		return null;
	}

	let imageData: ImageData;
	try {
		imageData = ctx.getImageData(x0, y0, rw, rh);
	} catch {
		return null;
	}

	const ps = FACE_TEXTURE_PATCH_SIZE;
	const patch = new Float32Array(ps * ps);
	let sum = 0;
	for (let py = 0; py < ps; py++) {
		for (let px = 0; px < ps; px++) {
			const sx = Math.min(rw - 1, Math.floor(((px + 0.5) / ps) * rw));
			const sy = Math.min(rh - 1, Math.floor(((py + 0.5) / ps) * rh));
			const i = (sy * rw + sx) * 4;
			const luma = 0.299 * imageData.data[i] + 0.587 * imageData.data[i + 1] + 0.114 * imageData.data[i + 2];
			patch[py * ps + px] = luma;
			sum += luma;
		}
	}
	const mean = sum / patch.length;
	for (let i = 0; i < patch.length; i++) {
		patch[i] -= mean;
	}

	let lapSum = 0;
	let lapSq = 0;
	let lapN = 0;
	for (let py = 1; py < ps - 1; py++) {
		for (let px = 1; px < ps - 1; px++) {
			const c = patch[py * ps + px];
			const lap =
				4 * c -
				patch[py * ps + (px - 1)] -
				patch[py * ps + (px + 1)] -
				patch[(py - 1) * ps + px] -
				patch[(py + 1) * ps + px];
			lapSum += lap;
			lapSq += lap * lap;
			lapN++;
		}
	}
	const lapMean = lapN > 0 ? lapSum / lapN : 0;
	const laplacianVar = lapN > 0 ? Math.max(0, lapSq / lapN - lapMean * lapMean) : 0;

	return { laplacianVar, patch16: patch };
}

/** Cara centrada en el óvalo (heurística lab turns-3d; depth-real usa face-lab-middle-face.util). */
export function isFaceCenteredInOval(
	snapshot: FaceGeometrySnapshot,
	options: { maxOffsetX?: number; maxOffsetY?: number; minHeight?: number; maxHeight?: number } = {},
): boolean {
	const maxOffsetX = options.maxOffsetX ?? 0.14;
	const maxOffsetY = options.maxOffsetY ?? 0.16;
	const minHeight = options.minHeight ?? 0.22;
	const maxHeight = options.maxHeight ?? 0.62;
	return (
		Math.abs(snapshot.centerX - 0.5) <= maxOffsetX &&
		Math.abs(snapshot.centerY - 0.45) <= maxOffsetY &&
		snapshot.bboxHeight >= minHeight &&
		snapshot.bboxHeight <= maxHeight
	);
}
